
import React, { useState } from 'react';
import useMounted from '../../hooks/useMounted';
import { post } from '../../helpers/api_helper';
import { amountFormatUse } from '../../helpers/Helper';
import { GET_ADMIN_DASHBOARD_DATA } from '../../helpers/url_helper';
import { toast } from 'react-toastify';
import Chart from 'react-apexcharts';

const iconStyle = (bgColor) => ({
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: bgColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
});

const Dashboard = (props) => {
    const [totalUser, SetTotalUser] = useState(0);
    const [monthlyIncome, SetMonthlyIncome] = useState(0);
    const [yearIncome, SetYearIncome] = useState(0);
    const [lifetimeIncome, SetLifetimeIncome] = useState(0);
    const [totalPendingOrder, SetTotalPendingOrder] = useState(0);
    const [totalActiveSubscriptions, SetTotalActiveSubscriptions] = useState(0);
    const [totalInvoices, SetTotalInvoices] = useState(0);
    const [totalNewEnquiries, SetTotalNewEnquiries] = useState(0);
    const [monthlyIncomeData, SetMonthlyIncomeData] = useState([]);
    const [chequeIssuedList, SetChequeIssuedList] = useState([]);
    const [planWiseData, SetPlanWiseData] = useState([]);
    const [newUsers, SetNewUsers] = useState(0);
    const [renewedUsers, SetRenewedUsers] = useState(0);
    const [renewalPercentage, SetRenewalPercentage] = useState(0);
    const [monthlySubData, SetMonthlySubData] = useState([]);
    const [subscriptionStatusData, SetSubscriptionStatusData] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        var res_data = await post(GET_ADMIN_DASHBOARD_DATA, { offset: new Date().getTimezoneOffset() });
        if (res_data.status) {
            SetTotalUser(res_data.data.totalUser);
            SetMonthlyIncome(res_data.data.monthlyIncome);
            SetYearIncome(res_data.data.yearIncome);
            SetLifetimeIncome(res_data.data.lifetimeIncome);
            SetTotalPendingOrder(res_data.data.totalPendingOrder);
            SetTotalActiveSubscriptions(res_data.data.totalActiveSubscriptions);
            SetTotalInvoices(res_data.data.totalInvoices);
            SetTotalNewEnquiries(res_data.data.totalNewEnquiries);
            SetMonthlyIncomeData(res_data.data.monthlyIncomeData || []);
            SetChequeIssuedList(res_data.data.chequeIssuedList || []);
            SetPlanWiseData(res_data.data.planWiseData || []);
            SetNewUsers(res_data.data.newUsers || 0);
            SetRenewedUsers(res_data.data.renewedUsers || 0);
            SetRenewalPercentage(res_data.data.renewalPercentage || 0);
            SetMonthlySubData(res_data.data.monthlySubData || []);
            SetSubscriptionStatusData(res_data.data.subscriptionStatusData || []);
        } else {
            toast.error(res_data.message);
        }
        setLoading(false);
    };

    useMounted(() => {
        fetchData();
    }, []);

    // Income bar chart
    const incomeChartOptions = {
        chart: { type: 'bar', height: 350, toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 6, columnWidth: '45%' } },
        dataLabels: {
            enabled: true,
            formatter: (val) => amountFormatUse(val),
            offsetY: -20,
            style: { fontSize: '11px', colors: ['#304758'] },
        },
        colors: ['#696cff'],
        xaxis: { categories: monthlyIncomeData.map((d) => d.month) },
        yaxis: { labels: { formatter: (val) => amountFormatUse(val) } },
        tooltip: { y: { formatter: (val) => amountFormatUse(val) } },
        grid: { borderColor: '#f1f1f1' },
    };
    const incomeChartSeries = [{ name: 'Income', data: monthlyIncomeData.map((d) => d.income) }];

    // Plan-wise donut chart
    const planDonutOptions = {
        chart: { type: 'donut', height: 300 },
        labels: planWiseData.map((d) => d.planName || 'Unknown'),
        colors: ['#696cff', '#ff9f43', '#28c76f', '#00b8d4', '#8c57ff', '#e83e8c'],
        legend: { position: 'bottom' },
        dataLabels: { enabled: true },
        plotOptions: { pie: { donut: { size: '60%' } } },
    };
    const planDonutSeries = planWiseData.map((d) => parseInt(d.count) || 0);

    // Renewal vs New donut chart
    const renewalDonutOptions = {
        chart: { type: 'donut', height: 300 },
        labels: ['New Users', 'Renewed Users'],
        colors: ['#00b8d4', '#696cff'],
        legend: { position: 'bottom' },
        dataLabels: { enabled: true, formatter: (val) => val.toFixed(1) + '%' },
        plotOptions: { pie: { donut: { size: '60%', labels: { show: true, total: { show: true, label: 'Renewal Rate', formatter: () => renewalPercentage + '%' } } } } },
    };
    const renewalDonutSeries = [newUsers, renewedUsers];

    // Monthly subscriptions area chart
    const monthlySubChartOptions = {
        chart: { type: 'area', height: 300, toolbar: { show: false }, sparkline: { enabled: false } },
        stroke: { curve: 'smooth', width: 2 },
        colors: ['#28c76f'],
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 } },
        xaxis: { categories: monthlySubData.map((d) => d.month) },
        yaxis: { labels: { formatter: (val) => Math.round(val) } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (val) => val + ' subscriptions' } },
        grid: { borderColor: '#f1f1f1' },
    };
    const monthlySubChartSeries = [{ name: 'Subscriptions', data: monthlySubData.map((d) => d.count) }];

    // Subscription status bar chart
    const statusColors = { 'Active': '#28c76f', 'Expired': '#ff4c51', 'Cancelled': '#ff9f43' };
    const subStatusChartOptions = {
        chart: { type: 'bar', height: 300, toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 6, horizontal: true, distributed: true } },
        colors: subscriptionStatusData.map((d) => statusColors[d.status] || '#8c57ff'),
        xaxis: { categories: subscriptionStatusData.map((d) => d.status) },
        dataLabels: { enabled: true },
        legend: { show: false },
        grid: { borderColor: '#f1f1f1' },
    };
    const subStatusChartSeries = [{ name: 'Count', data: subscriptionStatusData.map((d) => d.count) }];

    return (
        <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1">Dashboard</h4>
                    <p className="text-muted mb-0">Welcome to the admin panel</p>
                </div>
                <button className="btn btn-outline-secondary" onClick={fetchData} disabled={loading}>
                    <i className="bx bx-revision me-1"></i>
                    Refresh
                </button>
            </div>

            {/* Row 1: 5 Stat Cards */}
            <div className="row g-4 mb-4">
                <div className="col">
                    <div className="card h-100 mb-0">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div style={iconStyle('#e7e7ff')}>
                                <i className="bx bxs-group" style={{ fontSize: '22px', color: '#696cff' }}></i>
                            </div>
                            <div>
                                <small className="text-muted">Total Customers</small>
                                <h4 className="mb-0 fw-bold">{loading ? '...' : totalUser}</h4>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col">
                    <div className="card h-100 mb-0">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div style={iconStyle('#fff2e2')}>
                                <i className="bx bxs-cart-download" style={{ fontSize: '22px', color: '#ff9f43' }}></i>
                            </div>
                            <div>
                                <small className="text-muted">Pending Orders</small>
                                <h4 className="mb-0 fw-bold">{loading ? '...' : totalPendingOrder}</h4>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col">
                    <div className="card h-100 mb-0">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div style={iconStyle('#ffecd9')}>
                                <i className="bx bxs-badge-check" style={{ fontSize: '22px', color: '#e5a635' }}></i>
                            </div>
                            <div>
                                <small className="text-muted">Active Subscriptions</small>
                                <h4 className="mb-0 fw-bold">{loading ? '...' : totalActiveSubscriptions}</h4>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col">
                    <div className="card h-100 mb-0">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div style={iconStyle('#ede4ff')}>
                                <i className="bx bxs-receipt" style={{ fontSize: '22px', color: '#8c57ff' }}></i>
                            </div>
                            <div>
                                <small className="text-muted">Total Invoices</small>
                                <h4 className="mb-0 fw-bold">{loading ? '...' : totalInvoices}</h4>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col">
                    <div className="card h-100 mb-0">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div style={iconStyle('#ffe0eb')}>
                                <i className="bx bxs-message-dots" style={{ fontSize: '22px', color: '#e83e8c' }}></i>
                            </div>
                            <div>
                                <small className="text-muted">New Enquiries</small>
                                <h4 className="mb-0 fw-bold">{loading ? '...' : totalNewEnquiries}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 2: 3 Income Cards */}
            <div className="row g-4 mb-4">
                <div className="col-md-4">
                    <div className="card h-100 mb-0">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div style={iconStyle('#d4f4e2')}>
                                <i className="bx bx-rupee" style={{ fontSize: '24px', color: '#28c76f' }}></i>
                            </div>
                            <div>
                                <small className="text-muted">Current Month Income</small>
                                <h4 className="mb-0 fw-bold">{loading ? '...' : amountFormatUse(monthlyIncome)}</h4>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card h-100 mb-0">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div style={iconStyle('#d0f5f5')}>
                                <i className="bx bx-rupee" style={{ fontSize: '24px', color: '#00b8d4' }}></i>
                            </div>
                            <div>
                                <small className="text-muted">Current Year Income</small>
                                <h4 className="mb-0 fw-bold">{loading ? '...' : amountFormatUse(yearIncome)}</h4>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card h-100 mb-0">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div style={iconStyle('#e0e0ff')}>
                                <i className="bx bx-trending-up" style={{ fontSize: '24px', color: '#7c7cf7' }}></i>
                            </div>
                            <div>
                                <small className="text-muted">Lifetime Income</small>
                                <h4 className="mb-0 fw-bold">{loading ? '...' : amountFormatUse(lifetimeIncome)}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 3: Income Chart */}
            {monthlyIncomeData.length > 0 && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-3">
                                    <i className="bx bx-trending-up me-2" style={{ fontSize: '20px' }}></i>
                                    <h5 className="mb-0">Income - Last 12 Months</h5>
                                </div>
                                <Chart options={incomeChartOptions} series={incomeChartSeries} type="bar" height={350} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Row 4: Subscription Charts */}
            <div className="row g-4 mb-4">
                {/* Plan-wise Distribution */}
                {planWiseData.length > 0 && (
                    <div className="col-md-6">
                        <div className="card h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-3">
                                    <i className="bx bx-pie-chart-alt-2 me-2" style={{ fontSize: '20px' }}></i>
                                    <h5 className="mb-0">Active Subscriptions by Plan</h5>
                                </div>
                                <Chart options={planDonutOptions} series={planDonutSeries} type="donut" height={300} />
                            </div>
                        </div>
                    </div>
                )}

                {/* New vs Renewed Users */}
                {(newUsers > 0 || renewedUsers > 0) && (
                    <div className="col-md-6">
                        <div className="card h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div className="d-flex align-items-center">
                                        <i className="bx bx-refresh me-2" style={{ fontSize: '20px' }}></i>
                                        <h5 className="mb-0">New vs Renewed Users</h5>
                                    </div>
                                    <span className="badge bg-label-success rounded-pill">{renewalPercentage}% Renewal Rate</span>
                                </div>
                                <Chart options={renewalDonutOptions} series={renewalDonutSeries} type="donut" height={300} />
                                <div className="d-flex justify-content-around mt-3">
                                    <div className="text-center">
                                        <h5 className="mb-0 fw-bold">{newUsers}</h5>
                                        <small className="text-muted">New Users</small>
                                    </div>
                                    <div className="text-center">
                                        <h5 className="mb-0 fw-bold">{renewedUsers}</h5>
                                        <small className="text-muted">Renewed Users</small>
                                    </div>
                                    <div className="text-center">
                                        <h5 className="mb-0 fw-bold">{newUsers + renewedUsers}</h5>
                                        <small className="text-muted">Total</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Row 5: Monthly Subscriptions + Status */}
            <div className="row g-4 mb-4">
                {/* Monthly Subscriptions Trend */}
                {monthlySubData.length > 0 && (
                    <div className="col-md-7">
                        <div className="card h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-3">
                                    <i className="bx bx-line-chart me-2" style={{ fontSize: '20px' }}></i>
                                    <h5 className="mb-0">Subscriptions - Last 12 Months</h5>
                                </div>
                                <Chart options={monthlySubChartOptions} series={monthlySubChartSeries} type="area" height={300} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Subscription Status Breakdown */}
                {subscriptionStatusData.length > 0 && (
                    <div className="col-md-5">
                        <div className="card h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-3">
                                    <i className="bx bx-bar-chart me-2" style={{ fontSize: '20px' }}></i>
                                    <h5 className="mb-0">Subscription Status</h5>
                                </div>
                                <Chart options={subStatusChartOptions} series={subStatusChartSeries} type="bar" height={300} />
                                <div className="mt-3">
                                    {subscriptionStatusData.map((item, idx) => (
                                        <div key={idx} className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="d-flex align-items-center">
                                                <span
                                                    className="rounded-circle d-inline-block me-2"
                                                    style={{ width: '10px', height: '10px', backgroundColor: statusColors[item.status] || '#8c57ff' }}
                                                ></span>
                                                <span>{item.status}</span>
                                            </div>
                                            <strong>{item.count}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Row 6: Last 10 Cheques Issued */}
            {chequeIssuedList.length > 0 && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header d-flex align-items-center justify-content-between pb-0">
                                <div className="card-title mb-0">
                                    <h5 className="mb-0">Recent Cheques Issued</h5>
                                    <small className="text-muted">Last 10 cheques</small>
                                </div>
                                <span className="badge bg-label-primary">{chequeIssuedList.length} Records</span>
                            </div>
                            <div className="card-body pt-3">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '60px' }}>#</th>
                                                <th>User</th>
                                                <th>Bank</th>
                                                <th>Payee Name</th>
                                                <th>Amount</th>
                                                <th>Date</th>
                                                <th>Type</th>
                                            </tr>
                                        </thead>
                                        <tbody className="table-border-bottom-0">
                                            {chequeIssuedList.map((data) => (
                                                <tr key={data.chequeId}>
                                                    <td><strong>{data.chequeId}</strong></td>
                                                    <td>
                                                        <span
                                                            className="text-primary fw-semibold"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => { window.open('/admin/users/view/' + data.userId, '_blank'); }}
                                                        >
                                                            <i className="bx bx-user me-1" style={{ fontSize: '14px' }}></i>
                                                            {data.userName}
                                                        </span>
                                                    </td>
                                                    <td>{data.bankName}</td>
                                                    <td>{data.name}</td>
                                                    <td><span className="fw-semibold">{amountFormatUse(data.amount)}</span></td>
                                                    <td>{data.date}</td>
                                                    <td>
                                                        <span className={`badge bg-label-${data.chequeType === 'Account Payee' ? 'info' : 'warning'} rounded-pill`}>
                                                            {data.chequeType}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Dashboard;
